import type {
  MsaCalculationPayload,
  MsaCalculationResponse,
  MsaMode,
} from "../features/msa/msaTypes";
import type { AppDispatch } from "../store";
import { setMsaJobId } from "../features/msa/msaSlice";
import { fetchProjectExternalData } from "./helperBackendApiService";

/**
 * Base URL for the OpenEO Connector microservice
 */
const OPENEO_CONNECTOR_BASE_URL =
  "https://bintact-openeo-connector-741920004150.europe-west1.run.app";

/**
 * Calculate MSA by calling the OpenEO Connector microservice
 * @param payload - The calculation payload including land use URI, bounds, etc.
 * @returns Promise resolving to the response containing job_id
 */
export async function calculateMsa(
  payload: MsaCalculationPayload
): Promise<MsaCalculationResponse> {
  const endpoint = `${OPENEO_CONNECTOR_BASE_URL}/v1/api/calculate-msa`;

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `MSA calculation request failed: ${response.status} ${response.statusText}. ${errorText}`
      );
    }

    const data: MsaCalculationResponse = await response.json();

    if (!data.job_id) {
      throw new Error("MSA calculation response missing job_id");
    }

    return data;
  } catch (error) {
    console.error("Failed to start MSA calculation:", error);
    throw error;
  }
}

/**
 * Get the SSE endpoint URL for a given job ID
 * @param jobId - The job ID to subscribe to
 * @returns The full SSE endpoint URL
 */
export function getMsaEventStreamUrl(jobId: string): string {
  return `${OPENEO_CONNECTOR_BASE_URL}/v1/events/jobs/${jobId}`;
}

/**
 * Fetch project external data, start an MSA calculation, and dispatch the job ID to Redux.
 * Silently logs errors without rethrowing so callers don't need to handle exceptions.
 */
export async function triggerMsaCalculation(
  projectId: string,
  mode: MsaMode,
  dispatch: AppDispatch
): Promise<void> {
  try {
    const data = await fetchProjectExternalData(projectId);

    // Fall back to baseline raster when project mode is requested but patchLayerId is missing
    const effectiveMode = mode === "project" && !data.patchLayerId ? "baseline" : mode;
    const landUseUri = effectiveMode === "baseline" ? data.baseRasterUri : data.patchLayerId;

    const payload = {
      land_use_uri: landUseUri,
      roads_uri: null,
      export: true,
      area_of_interest: data.projectAreaGeoJSON ?? undefined,
      export_id: `${projectId}-${effectiveMode}`,
    }

    const msaResponse = await calculateMsa(data.owner?.userId ? { ...payload, user_id: data.owner?.userId } : payload);

    dispatch(
      setMsaJobId({
        projectId,
        mode: effectiveMode,
        jobId: msaResponse.job_id,
      })
    );
  } catch (error) {
    console.error(`Failed to auto-trigger MSA calculation (mode: ${mode}):`, error);
  }
}

